import { getState } from "../state.ts";
import { inlineHtmlAssets } from "./asset-inline.ts";
import { rebuildModuleIndex } from "./rebuild.ts";
import type { ModuleIndexData } from "../../modules/shared/module-index.ts";
import type { ModuleId } from "../../auth/config.ts";

/**
 * Where the viewer gets its data. Abstracted behind an interface so the UI
 * (viewer.ts) can be driven by a fake in tests/harnesses, while the real app
 * reads from the chosen destination folder via the File System Access API.
 */

export interface HtmlItem {
    /** Body HTML with relative assets already resolved to blob: URLs. */
    bodyHtml: string;
    /** Releases every blob URL this item created. Call when leaving the item. */
    revoke: () => void;
}

export interface FileItem {
    url: string;
    name: string;
    /** MIME type, best-effort from the File — "" when the OS didn't supply one. */
    type: string;
    revoke: () => void;
}

export interface ViewerSource {
    /** A module's index (rows/columns/counts), or null if it was never exported here. */
    loadIndex(moduleId: ModuleId): Promise<ModuleIndexData | null>;
    /** For itemKind "html": the item's renderable body. */
    loadHtml(moduleId: ModuleId, href: string): Promise<HtmlItem>;
    /** For itemKind "file": a blob URL for the file itself. */
    loadFile(moduleId: ModuleId, href: string): Promise<FileItem>;
}

export class NoFolderError extends Error {
    constructor() {
        super("No destination folder is connected");
        this.name = "NoFolderError";
    }
}

function splitPath(path: string): { dirs: string[]; name: string } {
    const segments = path.split("/").filter((s) => s.length > 0);
    const name = segments.pop() ?? "";
    return { dirs: segments, name };
}

async function walkTo(
    root: FileSystemDirectoryHandle,
    dirs: readonly string[]
): Promise<FileSystemDirectoryHandle> {
    let dir = root;
    for (const segment of dirs) {
        dir = await dir.getDirectoryHandle(segment);
    }
    return dir;
}

/**
 * The real source. Reads the same on-disk names the export wrote, so hrefs in
 * index.json map straight to files without re-sanitizing (every href was built
 * from an already-sanitized on-disk name).
 */
export function createFsaViewerSource(): ViewerSource {
    const root = (): FileSystemDirectoryHandle => {
        const handle = getState().folderHandle;
        if (!handle) {
            throw new NoFolderError();
        }
        return handle;
    };

    return {
        async loadIndex(moduleId) {
            const rootHandle = root();

            let dir: FileSystemDirectoryHandle | null = null;
            try {
                dir = await rootHandle.getDirectoryHandle(moduleId);
            } catch {
                dir = null;
            }

            // Fast path: the sidecar written at export time.
            if (dir) {
                try {
                    const handle = await dir.getFileHandle("index.json");
                    const text = await (await handle.getFile()).text();
                    return JSON.parse(text) as ModuleIndexData;
                } catch {
                    // No sidecar — fall through to rebuilding from raw files.
                }
            }

            // The sidecar is only written while an export runs; rebuild from
            // the raw files on disk so a folder from an interrupted or older
            // run still browses correctly (see rebuild.ts).
            return rebuildModuleIndex(rootHandle, moduleId);
        },

        async loadHtml(moduleId, href) {
            const { dirs, name } = splitPath(href);
            const itemDir = await walkTo(root(), [moduleId, ...dirs]);
            const handle = await itemDir.getFileHandle(name);
            const html = await (await handle.getFile()).text();

            const created: string[] = [];
            const bodyHtml = await inlineHtmlAssets(html, async (rel) => {
                const target = splitPath(rel);
                try {
                    const assetDir = await walkTo(itemDir, target.dirs);
                    const assetHandle = await assetDir.getFileHandle(target.name);
                    const url = URL.createObjectURL(await assetHandle.getFile());
                    created.push(url);
                    return url;
                } catch {
                    return null;
                }
            });

            return {
                bodyHtml,
                revoke: () => {
                    for (const url of created) {
                        URL.revokeObjectURL(url);
                    }
                }
            };
        },

        async loadFile(moduleId, href) {
            const { dirs, name } = splitPath(`${moduleId}/${href}`);
            const dir = await walkTo(root(), dirs);
            const handle = await dir.getFileHandle(name);
            const file = await handle.getFile();
            const url = URL.createObjectURL(file);
            return {
                url,
                name: file.name,
                type: file.type,
                revoke: () => URL.revokeObjectURL(url)
            };
        }
    };
}
